import type { VisualPreviewOptions, VisualPreviewResult } from './adminConfig';
import axios from 'axios';
import { useAuthStore } from '@/stores/authStore';
import { toast } from '@/lib/toast';
import i18n from '@/i18n';
import {
  Brand,
  CreateBrandRequest,
  UpdateBrandRequest,
  Post,
  CreatePostRequest,
  UpdatePostRequest,
  GeneratePostsRequest,
  GeneratePostsResponse,
  Workspace,
  CreateWorkspaceRequest,
  DashboardStats,
  User,
  PlatformConnection,
  OnboardingProfile,
  ApiKey,
  DataEventItem,
  ContentCadence,
  UpsertCadenceRequest,
  ReviewPost,
  AnalyticsOverview,
  PostAnalyticsItem,
  PostTimeseriesPoint,
  PlatformTimeseriesPoint,
  LinkedInOrgSummary,
  LinkedInOrgHistory,
  SocialPlatform,
  AdCampaign,
  AdSet,
  AdCreative,
  CampaignInsightsResponse,
  AdminUser,
  AdminUsageOverview,
  ManualPostRequest,
  WorkspaceAiUsage,
  AdminAiUsageSummary,
  AdminAiSettingsResponse,
  UpdateAdminAiSettingsRequest,
  AdminWorkspaceAiUsage,
  SchedulerConfig,
  ApiCallLogRow,
  ApiCallLogStats,
  ImportJob,
  BlogAnalyticsSummary,
} from '@/types';

import { api } from './client';

export const visualApi = {
  preview: (workspaceId: string, postId: string, body: VisualPreviewOptions) =>
    api
      .post<VisualPreviewResult>(
        `/api/workspaces/${workspaceId}/posts/${postId}/visual/preview`,
        body,
      )
      .then((r) => r.data),
  commit: (workspaceId: string, postId: string, body: { imageUrl: string; prompt?: string }) =>
    api
      .post(`/api/workspaces/${workspaceId}/posts/${postId}/visual/commit`, body)
      .then((r) => r.data),
  /**
   * Upload a user-supplied image and set it as the post's visual. Multipart
   * form with a single `file` part. Replaces any existing visual on the post.
   */
  upload: (workspaceId: string, postId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api
      .post(`/api/workspaces/${workspaceId}/posts/${postId}/visual/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};

// ── Reels ─────────────────────────────────────────────────────────────────
// Backend uses `regenerate-reel` (the "generate-reel" name in reviewApi was
// 404'ing in production — not a real endpoint).

export interface ReelStatus {
  videoGenerationStatus: string | null;
  videoUrl: string | null;
  videoRawUrl: string | null;
  videoDurationSeconds: number | null;
  hasOverlay: boolean | null;
  /** ISO timestamp of when fal.ai generation started — drives the elapsed
   *  timer + honest ETA so a multi-minute audio reel doesn't read as frozen. */
  videoGenerationStartedAt: string | null;
}

export const reelApi = {
  /** Kicks off video generation. The backend writes the prompt + status; status
   *  flips to 'pending' → 'generating' → 'ready' (or 'failed') over ~60 seconds.
   *
   *  V114 — `includeAudio` routes the call to the audio-on fal.ai model
   *  (`reel_model_audio_on` system_setting) and charges against the
   *  MONTHLY_REELS_WITH_AUDIO cap. Defaults to false (audio-off, cheap).
   *  Workspace must have the REELS_WITH_AUDIO feature flag for the
   *  audio-on path to actually fire — backend silently downgrades to
   *  audio-off if the plan doesn't allow audio. */
  regenerate: (workspaceId: string, postId: string, includeAudio: boolean = false) =>
    api
      .post<{
        status: string;
      }>(`/api/workspaces/${workspaceId}/posts/${postId}/regenerate-reel`, { includeAudio })
      .then((r) => r.data),

  /** Poll while videoGenerationStatus !== ready/failed. Returns BOTH the
   *  overlay video and the raw (no-overlay) version when present. */
  status: (workspaceId: string, postId: string) =>
    api
      .get<ReelStatus>(`/api/workspaces/${workspaceId}/posts/${postId}/reel-status`)
      .then((r) => r.data),

  /** Toggle the branded text overlay. Re-rendering the overlay spawns a K8s
   *  Job in production (3Gi memory) — locally it runs in-process. Status flips
   *  to `rendering_overlay` until done. */
  toggleOverlay: (workspaceId: string, postId: string, overlay: boolean, watermark = false) =>
    api
      .post<{
        videoUrl: string | null;
        hasOverlay: boolean;
        overlayStatus: string | null;
      }>(`/api/workspaces/${workspaceId}/posts/${postId}/reel-overlay`, { overlay, watermark })
      .then((r) => r.data),
};

export const brandApi = {
  list: (workspaceId: string) =>
    api.get<Brand[]>(`/api/workspaces/${workspaceId}/brands`).then((r) => r.data),
  create: (workspaceId: string, data: CreateBrandRequest) =>
    api.post<Brand>(`/api/workspaces/${workspaceId}/brands`, data).then((r) => r.data),
  update: (workspaceId: string, brandId: string, data: UpdateBrandRequest) =>
    api.patch<Brand>(`/api/workspaces/${workspaceId}/brands/${brandId}`, data).then((r) => r.data),
  delete: (workspaceId: string, brandId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/brands/${brandId}`).then((r) => r.data),
  uploadLogo: (workspaceId: string, brandId: string, file: File, variant: 'dark' | 'light') => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<Brand>(
        `/api/workspaces/${workspaceId}/brands/${brandId}/logo?variant=${variant}`,
        form,
        {
          headers: { 'Content-Type': 'multipart/form-data' },
        },
      )
      .then((r) => r.data);
  },
};

// V150 — Carousel Design Studio API. Templates live under
// /api/workspaces/:id/carousel-templates and surface both system
// (workspace_id=null) and workspace-scoped rows. Pro+ feature
// gate is enforced server-side.
export interface CarouselTemplateSummary {
  id: string;
  workspaceId: string | null;
  name: string;
  slug: string;
  description: string | null;
  thumbnailUrl: string | null;
  isDefault: boolean;
  source: 'system' | 'workspace';
  /** V150 — full template JSON, returned on the list endpoint so the
   *  settings page can render inline thumbnails entirely client-side
   *  (no Puppeteer, no PDF). */
  templateData: Record<string, unknown>;
  createdAt: string | null;
  updatedAt: string | null;
}

export interface CarouselTemplateDetail extends CarouselTemplateSummary {
  templateData: Record<string, unknown>;
}

export interface CreateCarouselTemplatePayload {
  name: string;
  slug?: string;
  description?: string;
  sourceTemplateId?: string;
  templateData?: Record<string, unknown>;
}

export interface UpdateCarouselTemplatePayload {
  name?: string;
  description?: string;
  templateData?: Record<string, unknown>;
}

export const carouselTemplateApi = {
  list: (workspaceId: string) =>
    api
      .get<CarouselTemplateSummary[]>(`/api/workspaces/${workspaceId}/carousel-templates`)
      .then((r) => r.data),
  get: (workspaceId: string, templateId: string) =>
    api
      .get<CarouselTemplateDetail>(
        `/api/workspaces/${workspaceId}/carousel-templates/${templateId}`,
      )
      .then((r) => r.data),
  create: (workspaceId: string, payload: CreateCarouselTemplatePayload) =>
    api
      .post<CarouselTemplateDetail>(`/api/workspaces/${workspaceId}/carousel-templates`, payload)
      .then((r) => r.data),
  update: (workspaceId: string, templateId: string, payload: UpdateCarouselTemplatePayload) =>
    api
      .put<CarouselTemplateDetail>(
        `/api/workspaces/${workspaceId}/carousel-templates/${templateId}`,
        payload,
      )
      .then((r) => r.data),
  delete: (workspaceId: string, templateId: string) =>
    api.delete(`/api/workspaces/${workspaceId}/carousel-templates/${templateId}`),
  preview: (workspaceId: string, templateId: string) =>
    api
      .post<{
        url: string;
      }>(`/api/workspaces/${workspaceId}/carousel-templates/${templateId}/preview`)
      .then((r) => r.data),
  uploadAsset: (workspaceId: string, templateId: string, file: File) => {
    const form = new FormData();
    form.append('file', file);
    return api
      .post<{
        url: string;
        key: string;
      }>(`/api/workspaces/${workspaceId}/carousel-templates/${templateId}/assets`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      .then((r) => r.data);
  },
};
